import _ from 'lodash';
import { useLayoutEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import { RiskGroupOption, SectorContext } from '../../context/sectors';
import { useGetSectors } from '../../queries/sectors';
import { useUserSeniority } from '../../queries/seniority';
import { useDebounce } from '../../util/hooks';
import { SectorDataInstance } from './SectorDataProvider.class';
import { SectorDataProviderProps } from './SectorDataProvider.types';

const useRiskGroupSelection = () => {
  const [selectedSectors, setSelectedSectors] = useState<RiskGroupOption[]>([]);
  const { isFetching: isFetchingSeniority } = useUserSeniority();
  const { data: sectorsQueryData, isFetching: isFetchingSectors } = useGetSectors(!isFetchingSeniority);

  const onRiskGroupChange = (selectedSectors: RiskGroupOption[]) => {
    // we don't allow to have no sectors selected
    SectorDataInstance.setSelectedSectorsIds(selectedSectors);
    selectedSectors.length !== 0 && setSelectedSectors(selectedSectors);
  };

  return {
    sectorsQueryData,
    onRiskGroupChange,
    isFetching: isFetchingSectors,
    selectedSectors,
    setSelectedSectors,
  };
};

const SectorDataProvider = ({ children }: SectorDataProviderProps) => {
  const queryClient = useQueryClient();
  const {
    sectorsQueryData: sectors,
    onRiskGroupChange,
    isFetching,
    selectedSectors,
    setSelectedSectors,
  } = useRiskGroupSelection();

  const sectorsQueryData = useMemo(() => _.sortBy(sectors, 'sectorBookName'), [sectors]);

  // We need to debounce to not make too many requests at once
  const debouncedSelectedSectors = useDebounce(selectedSectors, 700);

  useLayoutEffect(() => {
    // Reset all non-sector, without impersonation and user-seniority queries when sectors change
    // fixme: We should make a more precise way to reset only Theme queries
    if (debouncedSelectedSectors.length > 0 && !SectorDataInstance.areSectorsBlocked) {
      void queryClient.resetQueries({
        predicate: (query) =>
          // Warning - Do not remove this line, otherwise it will trigger a loop of resetting queries
          query.queryKey[0] !== 'impersonation' &&
          query.queryKey[0] !== 'user-seniority' &&
          query.queryKey[0] !== 'sectors',
      });
    }
  }, [debouncedSelectedSectors, queryClient]);

  return (
    <SectorContext.Provider
      value={{
        sectorsQueryData,
        selectedSectors,
        setSelectedSectors,
        isFetching,
        onRiskGroupChange,
      }}
    >
      {children}
    </SectorContext.Provider>
  );
};

export default SectorDataProvider;
